// Front-door eligibility rules for hosting Donuts with Diffit.
//
// These are pure functions over an `eligibilityData` snapshot (fetched from prod
// via DonutsWithDiffitService.getDonutsEligibility). Keeping them pure keeps the
// rules testable and lets the container decide how to render the outcome.
//
// Expected eligibilityData shape (see DonutsWithDiffitService for where it comes from):
//   {
//     acceptingResponses: boolean,      // global on/off switch (LaunchDarkly flag, backend-evaluated)
//     country: 'US' | string,           // normalized country code
//     hasSchoolOrDistrict: boolean,     // attached to a school or district
//     resourcesLast28d: number,         // resources generated in the last 28 days
//     sessionsUsed: number,             // applications submitted in the current period
//     sessionLimit: number,             // sessions allowed this period (by account tier)
//     bypassesHostRules: boolean,       // org admin or ambassador — skips the waivable rules
//   }

const ELIGIBLE_COUNTRIES = ['US'];

// Ordered rules — the first failing rule decides the outcome shown. Each rule:
// { code, passes(data) => boolean, reason, headline?, waivable? }. `code` lets the
// ineligible screen render a custom message (e.g. the school rule links to the
// onboarding wizard); `headline` overrides the default headline for that rule.
// `waivable` rules are skipped for org admins and ambassadors (`bypassesHostRules`);
// the global switch and the per-period limit apply to everyone.
const FRONT_DOOR_RULES = [
  {
    code: 'closed',
    passes: (data) => data.acceptingResponses !== false,
    headline: 'Applications are closed right now',
    reason: 'Donuts with Diffit isn’t accepting applications at the moment — check back soon!',
  },
  {
    code: 'country',
    waivable: true,
    passes: (data) => ELIGIBLE_COUNTRIES.includes(data.country),
    reason: 'Donuts with Diffit is currently only available to educators in the US.',
  },
  {
    code: 'school',
    waivable: true,
    passes: (data) => Boolean(data.hasSchoolOrDistrict),
    reason:
      'In order to host a Donuts with Diffit, you must update your profile with your school or district name.',
  },
  {
    code: 'resources',
    waivable: true,
    passes: (data) => Number(data.resourcesLast28d) >= 1,
    reason:
      'We ask that teachers who apply to host a Donuts with Diffit session have made a resource with Diffit in the last 28 days.',
  },
  {
    code: 'tierNotAllowed',
    // Not waivable, and it doesn't need to be: the only payload it rejects
    // (sessionLimit 0) also fails limitReached, which applies to everyone.
    // Fail only on an explicit 0 — a missing sessionLimit (older backend during
    // deploy skew) must not read as "not allowed".
    passes: (data) => data.sessionLimit !== 0,
    reason: 'Donuts with Diffit is currently only available to paid accounts.',
  },
  {
    code: 'limitReached',
    // Negated >= so missing fields (NaN comparisons are false) pass rather than block.
    passes: (data) => !(Number(data.sessionsUsed) >= Number(data.sessionLimit)),
    reason:
      'Teachers are only eligible to host an event every 3 months. Check back once a few months have passed!',
  },
];

// Returns { eligible: true } or { eligible: false, reason, code?, headline? }.
export function checkFrontDoorEligibility(eligibilityData) {
  if (!eligibilityData) return { eligible: false, reason: 'We couldn’t verify your eligibility.' };
  for (const rule of FRONT_DOOR_RULES) {
    if (rule.waivable && eligibilityData.bypassesHostRules) continue;
    if (!rule.passes(eligibilityData)) {
      return { eligible: false, reason: rule.reason, code: rule.code, headline: rule.headline };
    }
  }
  return { eligible: true };
}

// Shared headline for every ineligible outcome (front-door or mid-form).
export const INELIGIBLE_HEADLINE =
  'Sorry, you’re not eligible to host Donuts with Diffit at this time';
